import { getAuthHeaders } from "../AuthHeader/getAuthHeader";
import api from "../Interceptors";
import { ApiError } from "../ApiError";
import { isPresignedUrlExpired } from "../../utils/util";

/** Allowed extension for IFC uploads (lowercase). */
export const IFC_ALLOWED_EXTENSIONS = [".ifc"] as const;

/** MIME type used for IFC files when sending to backend. */
export const IFC_FILE_TYPE = "application/ifc";

/** Allowed extensions for control panel (Excel) uploads (lowercase). */
export const EXCEL_ALLOWED_EXTENSIONS = [".xlsx", ".xls"] as const;

/** Allowed report document extensions (lowercase). */
export const REPORT_ALLOWED_EXTENSIONS = [
  ".xlsx", ".xls", ".csv", ".pdf", ".doc", ".docx", ".zip",
] as const;

/** MIME types accepted for IFC (browsers may send .ifc as application/octet-stream). */
const VALID_IFC_MIME_TYPES = ["application/ifc", "application/octet-stream", ""];

/** MIME types accepted for Excel uploads. Empty string removed so any file cannot pass MIME validation. */
const VALID_EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/octet-stream",
];

const EXCEL_MAX_BYTES = 50 * 1024 * 1024; // 50MB
const IFC_MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2GB
const THUMBNAIL_MAX_BYTES = 5 * 1024 * 1024; // 5MB

/** XLSX (Office Open XML) files are ZIP: first 4 bytes PK\x03\x04. */
const EXCEL_XLSX_MAGIC = [0x50, 0x4b, 0x03, 0x04];
/** XLS (OLE) files: first 4 bytes D0 CF 11 E0. */
const EXCEL_XLS_MAGIC = [0xd0, 0xcf, 0x11, 0xe0];

/** IFC (STEP) files start with "ISO-" per ISO-10303-21. */
const IFC_MAGIC_BYTES = "ISO-";

type SignedUploadFile = {
  key?: string;
  uploadUrl?: string;
  error?: string;
  uploadUrlExpiresAt?: string;
  uploadUrlExpiresIn?: number;
};

const EXPIRY_SKEW_MS = 30_000;
const EXPIRED_UPLOAD_MESSAGE = "Upload URL expired. Please retry.";

function matchesMagic(bytes: Uint8Array, magic: number[]): boolean {
  return magic.every((b, i) => bytes[i] === b);
}

function isExpiredByTimestamp(expiresAt?: string, skewMs: number = EXPIRY_SKEW_MS): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt);
  if (!Number.isFinite(ms)) return false;
  return Date.now() + skewMs >= ms;
}

function isUploadUrlExpired(signed: SignedUploadFile): boolean {
  if (isExpiredByTimestamp(signed.uploadUrlExpiresAt)) return true;
  return !!signed.uploadUrl && isPresignedUrlExpired(signed.uploadUrl);
}

async function requestSignedUploadFiles(
  files: Array<{ filename: string; fileType: string; fileSize: number }>,
  type: string,
): Promise<SignedUploadFile[]> {
  const authHeader = await getAuthHeaders();
  const config = {
    headers: {
      "Content-Type": "application/json",
      ...authHeader,
    },
  };
  const uploadUrlResponse = await api.post(
    "/upload-url",
    {
      files,
      type,
    },
    config
  );
  return uploadUrlResponse.data.files as SignedUploadFile[];
}

/**
 * Validates Excel file: extension, size, MIME type, and magic bytes (XLSX/XLS). Client-side only; backend must validate content.
 * @returns Promise resolving to error message string if invalid, null if valid.
 */
export async function validateExcelFile(file: File): Promise<string | null> {
  const name = (file.name || "").trim();
  if (!name) return "Please select a file.";
  const lower = name.toLowerCase();
  const hasAllowedExt = EXCEL_ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
  if (!hasAllowedExt) {
    return "Invalid file type. Only Excel files are allowed (.xlsx, .xls).";
  }
  const mime = (file.type || "").trim();
  if (!VALID_EXCEL_MIME_TYPES.includes(mime)) {
    return "Invalid file type. Only Excel files are allowed (.xlsx, .xls).";
  }
  if (file.size > EXCEL_MAX_BYTES) return "Excel files must be under 50MB.";
  const headerBytes = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(headerBytes);
  const isXlsx = matchesMagic(bytes, EXCEL_XLSX_MAGIC);
  const isXls = matchesMagic(bytes, EXCEL_XLS_MAGIC);
  if (!isXlsx && !isXls) {
    return "Invalid file content. File must be a valid Excel document (.xlsx or .xls).";
  }
  return null;
}

/**
 * Validates IFC file: extension, MIME type, size limit, and magic bytes (ISO-). Client-side only; backend must validate content.
 * @returns Promise resolving to error message string if invalid, null if valid.
 */
export async function validateIfcFile(file: File): Promise<string | null> {
  const name = (file.name || "").trim();
  if (!name) return "Please select a file.";
  const lower = name.toLowerCase();
  const hasAllowedExt = IFC_ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
  if (!hasAllowedExt) {
    return "Invalid file type. Only .ifc files are allowed.";
  }
  const mime = (file.type || "").trim();
  if (!VALID_IFC_MIME_TYPES.includes(mime)) {
    return "Invalid file type. Only .ifc files are allowed.";
  }
  if (file.size > IFC_MAX_BYTES) {
    return "IFC files must be under 2GB.";
  }
  const headerBytes = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(headerBytes);
  const header = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
  if (header !== IFC_MAGIC_BYTES) {
    return "Invalid file content. IFC files must start with ISO- (STEP format).";
  }
  return null;
}

/**
 * Validates report file: only allowed report document types can be uploaded.
 * @returns Error message string if invalid, null if valid.
 */
export function validateReportFile(file: File): string | null {
  const name = (file.name || "").trim();
  if (!name) return "Please select a file.";
  const lower = name.toLowerCase();
  const ext = lower.includes(".") ? "." + lower.split(".").pop()! : "";
  if (!REPORT_ALLOWED_EXTENSIONS.some((allowedExt) => allowedExt === ext)) {
    return "Invalid report file type. Allowed: .xlsx, .xls, .csv, .pdf, .doc, .docx, .zip.";
  }
  return null;
}

/** Resolve fileType for upload; use IFC type when appropriate. */
function getFileType(file: File, uploadType: string): string {
  const name = (file.name || "").toLowerCase();
  const isIfc =
    uploadType === "ifcModels" ||
    uploadType === "ifc" ||
    name.endsWith(".ifc");
  if (isIfc) return IFC_FILE_TYPE;
  if (file.type && file.type.trim() !== "") return file.type;
  return "application/octet-stream";
}

export const uploadThumbnail = async (file: File, type: string = "users"): Promise<string> => {
  try {
    if (type === "ifcModels") {
      const validationError = await validateIfcFile(file);
      if (validationError) throw new Error(validationError);
    }
    if (type === "controlPanel") {
      const validationError = await validateExcelFile(file);
      if (validationError) throw new Error(validationError);
    }
    if (type === "reports") {
      const validationError = validateReportFile(file);
      if (validationError) throw new Error(validationError);
    }
    if (type === "users" || (type !== "ifcModels" && type !== "controlPanel" && type !== "reports")) {
      if (file.size > THUMBNAIL_MAX_BYTES) throw new Error("Thumbnail files must be under 5MB.");
    }
    const requestedFile = {
      filename: file.name,
      fileType: getFileType(file, type),
      fileSize: file.size,
    };

    // Step 1: Get signed URL from backend
    let signedFiles = await requestSignedUploadFiles([requestedFile], type);
    let signedFile = signedFiles?.[0];

    if (!signedFile || signedFile.error) {
      throw new Error(signedFile?.error || "Failed to get signed URL");
    }
    if (isUploadUrlExpired(signedFile)) {
      signedFiles = await requestSignedUploadFiles([requestedFile], type);
      signedFile = signedFiles?.[0];
      if (!signedFile || signedFile.error) {
        throw new Error(signedFile?.error || "Failed to refresh upload URL");
      }
      if (isUploadUrlExpired(signedFile)) {
        throw new Error(EXPIRED_UPLOAD_MESSAGE);
      }
    }

    // Step 2: Upload file to S3 using signed URL
    const resolvedFileType = getFileType(file, type);
    const putToS3 = async (url: string) =>
      api.put(url, file, {
        headers: {
          "Content-Type": resolvedFileType,
        },
      });
    try {
      await putToS3(signedFile.uploadUrl as string);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 403) throw error;

      const refreshed = await requestSignedUploadFiles([requestedFile], type);
      const refreshedSigned = refreshed?.[0];
      if (!refreshedSigned || refreshedSigned.error || !refreshedSigned.uploadUrl) {
        throw new Error(EXPIRED_UPLOAD_MESSAGE);
      }
      if (isUploadUrlExpired(refreshedSigned)) {
        throw new Error(EXPIRED_UPLOAD_MESSAGE);
      }
      await putToS3(refreshedSigned.uploadUrl);
      signedFile = refreshedSigned;
    }

    // Step 3: Get download URL
    // const downloadUrlResponse = await api.post(
    //     "/download-url",
    //     { key: signedFile.key },
    //     config
    // );

    if (!signedFile?.key) {
      throw new Error("Missing uploaded file key");
    }
    return signedFile.key;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload multiple IFC files to S3 and return an array of S3 keys (same order as files).
 * Use with updateProjectFiles(projectId, { ifcModel: keys }).
 */
export const uploadIfcModels = async (files: File[], type: string = "ifcModels"): Promise<string[]> => {
  if (!files.length) return [];
  try {
    for (const file of files) {
      const err = await validateIfcFile(file);
      if (err) throw new Error(err);
    }
    const requestedFiles = files.map((file) => ({
      filename: file.name,
      fileType: getFileType(file, type),
      fileSize: file.size,
    }));
    let signedFiles = await requestSignedUploadFiles(requestedFiles, type);
    if (!Array.isArray(signedFiles) || signedFiles.length !== files.length) {
      throw new Error("Failed to get signed URLs for all files");
    }

    const hasExpired = signedFiles.some((signed) => isUploadUrlExpired(signed));
    if (hasExpired) {
      signedFiles = await requestSignedUploadFiles(requestedFiles, type);
      if (!Array.isArray(signedFiles) || signedFiles.length !== files.length) {
        throw new Error("Failed to refresh signed URLs for all files");
      }
      if (signedFiles.some((signed) => isUploadUrlExpired(signed))) {
        throw new Error(EXPIRED_UPLOAD_MESSAGE);
      }
    }

    const keys: string[] = [];
    await Promise.all(
      signedFiles.map(async (signed, i) => {
        if (signed?.error) throw new Error(signed.error || "Failed to get signed URL");
        if (!signed.uploadUrl || !signed.key) throw new Error("Missing upload URL or key");
        const file = files[i];
        const resolvedFileType = getFileType(file, type);
        const putToS3 = async (url: string) =>
          api.put(url, file, {
            headers: { "Content-Type": resolvedFileType },
          });
        try {
          await putToS3(signed.uploadUrl);
          keys.push(signed.key);
        } catch (error: unknown) {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status !== 403) throw error;
          const refreshed = await requestSignedUploadFiles([requestedFiles[i]], type);
          const refreshedSigned = refreshed?.[0];
          if (!refreshedSigned || refreshedSigned.error || !refreshedSigned.uploadUrl || !refreshedSigned.key) {
            throw new Error(EXPIRED_UPLOAD_MESSAGE);
          }
          if (isUploadUrlExpired(refreshedSigned)) {
            throw new Error(EXPIRED_UPLOAD_MESSAGE);
          }
          await putToS3(refreshedSigned.uploadUrl);
          keys.push(refreshedSigned.key);
        }
      })
    );
    return keys;
  } catch (error) {
    throw new ApiError(error);
  }
};